// eslint-disable-next-line @typescript-eslint/no-explicit-any
declare let google: any;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
declare let window: any;
import { useEffect, useState } from 'react';

import useGoogleDrive from '../../../hooks/useGoogleDrive';
import {
  CallbackDoc,
  PickerCallback,
  PickerConfiguration,
  ViewIdOptions,
} from './typeDefs';
import useInjectScript from './useInjectScript';

export const useGoogleDrivePicker = (
  onFileSelected: (file: CallbackDoc) => void,
  tenantId: string,
  viewId: ViewIdOptions,
  viewMimeTypes?: string,
) => {
  const [loaded, error] = useInjectScript('https://apis.google.com/js/api.js');
  const [pickerApiLoaded, setpickerApiLoaded] = useState(false);
  // Set when the teacher asked for the picker but had to authenticate first.
  // The popup closes onto the page they started from, so without this they have
  // to go find the button again.
  const [isPickerPending, setIsPickerPending] = useState(false);

  const {
    metadata: googleDriveMetadata,
    authenticate: googleDriveAuthenticate,
    isReady: googleDriveIsReady,
  } = useGoogleDrive(tenantId);
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  const [handleFilePicker, setHandleFilePicker] = useState(() => () => {});

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  let picker: any;

  // open the picker
  const openPicker = (config: PickerConfiguration) => {
    // if we have token and everything is loaded open the picker
    if (config.token && loaded && !error && pickerApiLoaded) {
      return createPicker(config);
    }
  };

  // load the Drive picker api
  const loadApis = () => {
    window.gapi.load('picker', { callback: onPickerApiLoad });
  };

  const onPickerApiLoad = () => {
    setpickerApiLoaded(true);
  };

  const createPicker = ({
    token,
    supportDrives = false,
    viewId,
    disabled,
    multiselect,
    setOrigin,
    setParentFolder = '',
    viewMimeTypes,
    customViews,
    locale = 'en',
    setIncludeFolders,
    setSelectFolderEnabled,
    disableDefaultView = false,
    onLoad,
    onFileSelect,
    onCancel,
    onError,
  }: PickerConfiguration & { viewId: ViewIdOptions }) => {
    if (disabled) return false;

    const view = new google.picker.DocsView(google.picker.ViewId[viewId]);
    if (viewMimeTypes) view.setMimeTypes(viewMimeTypes);
    if (setIncludeFolders) view.setIncludeFolders(true);
    if (setSelectFolderEnabled) view.setSelectFolderEnabled(true);
    if (setParentFolder) view.setParent(setParentFolder);

    picker = new google.picker.PickerBuilder()
      .hideTitleBar()
      .setAppId(process.env.NEXT_PUBLIC_GOOGLE_APP_ID)
      .setOAuthToken(token)
      .setLocale(locale)
      .setCallback((data: PickerCallback) => {
        if (data.action === 'picked') {
          const selectedFile = data.docs[0];
          if (selectedFile.type !== 'folder') {
            onFileSelect && onFileSelect(data.docs[0]);
          }
        } else if (data.action === 'cancel') {
          onCancel && onCancel();
        } else if (data.action === 'error') {
          onError && onError(data);
        } else if (data.action === 'loaded') {
          onLoad && onLoad();
        }
      });

    if (setOrigin) {
      picker.setOrigin(setOrigin);
    }

    if (!disableDefaultView) {
      picker.addView(view);
    }

    if (customViews) {
      customViews.map(view => picker.addView(view));
    }

    if (multiselect) {
      picker.enableFeature(google.picker.Feature.MULTISELECT_ENABLED);
    }

    if (!supportDrives) {
      picker.enableFeature(google.picker.Feature.MINE_ONLY);
    }

    picker.build().setVisible(true);
    return true;
  };

  // get the apis from googleapis
  useEffect(() => {
    if (loaded && !error && !pickerApiLoaded) {
      loadApis();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loaded, error, pickerApiLoaded]);

  // Everything the picker needs can arrive in either order — the token from the
  // consent popup, and gapi finishing its load — so wait for both rather than
  // assuming auth is the slow one.
  useEffect(() => {
    if (
      !isPickerPending ||
      !googleDriveMetadata?.accessToken ||
      !loaded ||
      error ||
      !pickerApiLoaded
    ) {
      return;
    }
    setIsPickerPending(false);
    openPicker({
      token: googleDriveMetadata.accessToken.value,
      viewId,
      viewMimeTypes,
      supportDrives: true,
      multiselect: false,
      onFileSelect: onFileSelected,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPickerPending, googleDriveMetadata, loaded, error, pickerApiLoaded]);

  useEffect(() => {
    const newHandleFilePicker = async () => {
      if (!googleDriveIsReady || !googleDriveMetadata) {
        return;
      }
      if (!googleDriveMetadata?.accessToken) {
        setIsPickerPending(true);
        // Resolves when the popup closes. A teacher who backs out without
        // granting gets no token, and the request is dropped rather than left
        // armed to fire the next time one happens to arrive.
        const authenticated = await googleDriveAuthenticate();
        if (!authenticated?.accessToken) {
          setIsPickerPending(false);
        }
        return;
      }
      openPicker({
        token: googleDriveMetadata.accessToken.value,
        viewId,
        viewMimeTypes,
        supportDrives: true,
        multiselect: false,
        onFileSelect: onFileSelected,
      });
    };

    setHandleFilePicker(() => newHandleFilePicker);
    // `loaded` / `error` / `pickerApiLoaded` belong here: `openPicker` reads them
    // and returns silently when any is not ready, and the handler stored in
    // state closes over the values from the render this last ran in. Without
    // them, a mount where the Drive metadata is already cached — any second
    // visit, since the query key is shared — builds the handler before gapi has
    // finished loading and freezes `pickerApiLoaded: false` into it. The button
    // then does nothing at all, with no error, until the token refetch happens
    // to rebuild it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [googleDriveIsReady, googleDriveMetadata, loaded, error, pickerApiLoaded]);

  return {
    showFilePicker: handleFilePicker,
  };
};
